/**
 * Reform Title IV-D: Reclaim Our Families
 *
 * Landing page highlighting the devastating impact of Title IV-D child support enforcement
 * on families, children, and taxpayers. Advocates for urgent reform.
 */

import React, { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Link,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Modal,
  TextField,
  IconButton,
} from '@mui/material'
import {
  Gavel,
  Balance,
  AccountBalanceWallet,
  FamilyRestroom,
  Close as CloseIcon,
  HeartBroken,
} from '@mui/icons-material'
import DonateDialog from '@/components/DonateDialog'
import EmailSubscribeDialog from '@/components/EmailSubscribeDialog'
import SEOMetaTags from '@/components/SEOMetaTags'
import { donationContent } from '@/data'
import { getSEOConfig, getCanonicalUrl } from '@/utils/seo'
import heroImage from '@/assets/hero-child-support-reform.jpeg'
import cardImage1 from '@/assets/card-csr-1.jpeg'
import cardImage2 from '@/assets/card-csr-2.jpeg'
import cardImage3 from '@/assets/card-csr-3.jpeg'

/**
 * Child Support Reform Page Component
 */
const ChildSupportReform: React.FC = () => {
  // Get SEO configuration for this page
  const seoConfig = getSEOConfig('/issues/child-support-reform')!
  const canonicalUrl = getCanonicalUrl('/issues/child-support-reform')

  const [modalOpen, setModalOpen] = useState(false)
  const [isDonateDialogOpen, setIsDonateDialogOpen] = useState(false)
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false)

  const handleModalClose = () => setModalOpen(false)

  const handleDonateClick = () => setIsDonateDialogOpen(true)
  const handleDonateDialogClose = () => setIsDonateDialogOpen(false)

  const handleJoinClick = () => setIsEmailDialogOpen(true)
  const handleEmailDialogClose = () => setIsEmailDialogOpen(false)

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <Box component="main" role="main">
      {/* SEO Meta Tags */}
      <SEOMetaTags
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={canonicalUrl}
        ogImage={seoConfig.ogImage}
        keywords={seoConfig.keywords}
        noIndex={seoConfig.noIndex}
      />

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '60vh', md: '70vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          textAlign: 'center',
          px: 2,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
              mb: 3,
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
            }}
          >
            Title IV-D is Stealing Childhoods, One Order at a Time
          </Typography>
          <Typography
            variant="h5"
            component="p"
            sx={{
              fontSize: { xs: '1rem', md: '1.5rem' },
              mb: 4,
              maxWidth: '800px',
              mx: 'auto',
              color: 'white',
              textShadow: '1px 1px 3px rgba(0,0,0,0.8)',
            }}
          >
            12.7 million kids caught in the crossfire of a system that jails
            dads and breaks moms. It&apos;s time to heal.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => scrollToSection('stats')}
            aria-label="See the problem statistics"
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                bgcolor: 'secondary.dark',
              },
            }}
          >
            See the Problem
          </Button>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }} id="stats">
        <Typography
          variant="h2"
          component="h2"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '3rem' },
            mb: 6,
            color: 'brandColors.navy',
          }}
        >
          The Crisis in Numbers
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={3}
              sx={{
                height: '100%',
                textAlign: 'center',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-8px)' },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Gavel sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
                <Typography
                  variant="h4"
                  component="div"
                  sx={{ fontWeight: 700, mb: 1, color: 'secondary.main' }}
                >
                  1M+
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  Parents Jailed Yearly, Leaving Kids Fatherless
                </Typography>
                <Link
                  href="https://www.acf.hhs.gov/css"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                >
                  Source: HHS OCSS 2024
                </Link>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={3}
              sx={{
                height: '100%',
                textAlign: 'center',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-8px)' },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Balance
                  sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }}
                />
                <Typography
                  variant="h4"
                  component="div"
                  sx={{ fontWeight: 700, mb: 1, color: 'secondary.main' }}
                >
                  80%
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  Custody to Moms, Fueling $460B Arrears Trap
                </Typography>
                <Link
                  href="https://www.census.gov"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                >
                  Source: U.S. Census 2023
                </Link>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={3}
              sx={{
                height: '100%',
                textAlign: 'center',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-8px)' },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <AccountBalanceWallet
                  sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }}
                />
                <Typography
                  variant="h4"
                  component="div"
                  sx={{ fontWeight: 700, mb: 1, color: 'secondary.main' }}
                >
                  $6.5B
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  Taxpayer Spend on System That Recovers Just 30% of Debt
                </Typography>
                <Link
                  href="https://www.cbpp.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                >
                  Source: CBPP 2024
                </Link>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={3}
              sx={{
                height: '100%',
                textAlign: 'center',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-8px)' },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <FamilyRestroom
                  sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }}
                />
                <Typography
                  variant="h4"
                  component="div"
                  sx={{ fontWeight: 700, mb: 1, color: 'secondary.main' }}
                >
                  21M
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  Fatherless Children, Poverty Up 27%, Hearts Shattered
                </Typography>
                <Link
                  href="https://www.acf.hhs.gov/css"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                >
                  Source: ACF FY2024
                </Link>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Stories Section */}
      <Box sx={{ bgcolor: 'grey.100', py: { xs: 6, md: 10 } }} id="stories">
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '3rem' },
              mb: 2,
              color: 'brandColors.navy',
            }}
          >
            Real Stories, Real Pain
          </Typography>
          <Typography
            variant="h6"
            align="center"
            sx={{ mb: 6, fontStyle: 'italic', color: 'text.secondary' }}
          >
            Imagine your child&apos;s smile fading as handcuffs take their hero
            away
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card elevation={3} sx={{ height: '100%' }}>
                <Box
                  sx={{
                    height: 200,
                    backgroundImage: `url(${cardImage1})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(2px) grayscale(50%)',
                  }}
                />
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, color: 'secondary.main' }}
                  >
                    John&apos;s Story
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Jailed for $500 arrears, his daughter asks,{' '}
                    <strong>&quot;Where&apos;s Daddy?&quot;</strong> Now 5 years
                    without him. She draws pictures of empty chairs at dinner.
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                  >
                    &quot;My son lost his dad to jail over $200 arrears, now
                    he&apos;s fatherless forever.&quot;
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={3} sx={{ height: '100%' }}>
                <Box
                  sx={{
                    height: 200,
                    backgroundImage: `url(${cardImage2})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(2px) grayscale(50%)',
                  }}
                />
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, color: 'secondary.main' }}
                  >
                    Maria&apos;s Struggle
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Single mom crushed by $18K debt from her ex&apos;s arrears.
                    Works 3 jobs, kids cry at night.{' '}
                    <strong>&quot;System broke us both.&quot;</strong>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                  >
                    &quot;They took his license, now he can&apos;t even drive to
                    see them.&quot;
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={3} sx={{ height: '100%' }}>
                <Box
                  sx={{
                    height: 200,
                    backgroundImage: `url(${cardImage3})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(2px) grayscale(50%)',
                  }}
                />
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, color: 'secondary.main' }}
                  >
                    Tommy&apos;s Tears
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Age 8, hasn&apos;t seen Dad in 3 years. System punished Dad
                    for losing job.{' '}
                    <strong>&quot;Why don&apos;t you love me?&quot;</strong> he
                    writes.
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                  >
                    &quot;My daughter thinks I abandoned her. I was just
                    broke.&quot;
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Impact Visual - The Vicious Cycle */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography
          variant="h2"
          component="h2"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '3rem' },
            mb: 6,
            color: 'secondary.main',
          }}
        >
          The Vicious Cycle
        </Typography>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Stepper orientation="vertical" activeStep={-1}>
            <Step>
              <StepLabel
                StepIconProps={{
                  sx: {
                    color: 'secondary.main',
                    '& .MuiStepIcon-text': { fill: 'white' },
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  High Order Issued
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography>
                  Court sets unrealistic payment based on imputed income, not
                  reality.
                </Typography>
              </StepContent>
            </Step>

            <Step>
              <StepLabel
                StepIconProps={{
                  sx: {
                    color: 'secondary.main',
                    '& .MuiStepIcon-text': { fill: 'white' },
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Missed Payment
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography>
                  Parent loses job or gets sick, arrears pile up with interest.
                </Typography>
              </StepContent>
            </Step>

            <Step>
              <StepLabel
                StepIconProps={{
                  sx: {
                    color: 'secondary.main',
                    '& .MuiStepIcon-text': { fill: 'white' },
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Jail / License Loss
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography>
                  System jails parent or revokes license, no way to work now.
                </Typography>
              </StepContent>
            </Step>

            <Step>
              <StepLabel
                StepIconProps={{
                  sx: {
                    color: 'secondary.main',
                    '& .MuiStepIcon-text': { fill: 'white' },
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Broken Family
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography>
                  Kids lose parent, emotional trauma deepens, relationships
                  shatter.
                </Typography>
              </StepContent>
            </Step>

            <Step>
              <StepLabel
                StepIconProps={{
                  sx: {
                    color: 'secondary.main',
                    '& .MuiStepIcon-text': { fill: 'white' },
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  More Poverty
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography>
                  Both parents sink deeper, taxpayers fund cycle, kids suffer
                  lifelong.
                </Typography>
              </StepContent>
            </Step>
          </Stepper>

          <Box
            sx={{
              mt: 4,
              p: 3,
              bgcolor: 'warning.light',
              borderLeft: theme => `4px solid ${theme.palette.warning.main}`,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              <HeartBroken sx={{ verticalAlign: 'middle', mr: 1 }} />
              Break the Cycle
            </Typography>
            <Typography>
              Reform Title IV-D to prioritize love over ledgers. Healing starts
              with change.
            </Typography>
          </Box>
        </Paper>
      </Container>

      {/* Call to Action Section */}
      <Box
        sx={{
          bgcolor: 'secondary.main',
          color: 'white',
          py: { xs: 6, md: 10 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h2"
            gutterBottom
            color="white"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '3.5rem' },
              mb: 3,
            }}
          >
            Don&apos;t Let Another Child Cry Alone. Reform Now!
          </Typography>
          <Typography
            variant="h6"
            color="white"
            sx={{ mb: 4, maxWidth: '700px', mx: 'auto' }}
          >
            Join thousands demanding justice. Sign the petition, donate to the
            fight, or share this crisis.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 3,
              justifyContent: 'center',
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleJoinClick}
              aria-label="Join our movement - Subscribe to email updates"
              sx={{
                bgcolor: 'white',
                color: 'secondary.main',
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'background.accent',
                },
              }}
            >
              Join Our Movement
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleDonateClick}
              aria-label="Donate to the fight"
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  borderColor: 'background.accent',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Donate
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Share Your Story Modal */}
      <Modal
        open={modalOpen}
        onClose={handleModalClose}
        aria-labelledby="share-story-modal"
        aria-describedby="share-your-story-form"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: '90vh',
            overflow: 'auto',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography
              id="share-story-modal"
              variant="h5"
              component="h2"
              sx={{ fontWeight: 600 }}
            >
              Share Your Story
            </Typography>
            <IconButton onClick={handleModalClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Your voice matters. Help us fight for reform by sharing your
            experience.
          </Typography>

          <Box component="form" noValidate autoComplete="off">
            <TextField
              fullWidth
              label="Your Name"
              margin="normal"
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              margin="normal"
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Your Story"
              multiline
              rows={6}
              margin="normal"
              variant="outlined"
              placeholder="Tell us how Title IV-D has affected your family..."
            />
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              size="large"
              sx={{
                mt: 3,
                '&:hover': { bgcolor: 'secondary.dark' },
              }}
            >
              Submit Story
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Email Subscribe Dialog */}
      <EmailSubscribeDialog
        open={isEmailDialogOpen}
        onClose={handleEmailDialogClose}
      />

      {/* Donate Dialog */}
      <DonateDialog
        open={isDonateDialogOpen}
        onClose={handleDonateDialogClose}
        content={donationContent}
      />
    </Box>
  )
}

export default ChildSupportReform
